// =============================================================================
// FILE: packages/renderer/src/components/launcher/CyberpunkLauncher.tsx
//
// PURPOSE: The single unified command surface for BIM OPS. A sticky neon
//          launcher button + popover command palette. Every system-level
//          tool registers in launcherRegistry.ts and is routed/toggled
//          from here — no other UI surface introduces system-level entry
//          points.
//
// SHORTCUTS
//   Ctrl+K         → open / close launcher
//   Ctrl+Shift+V   → Quick Ingest (still works directly for keyboard users)
//   Ctrl+\         → Project Files (PFE)
// =============================================================================

import { useEffect, useMemo, useRef, useState } from 'react';
import type { JSX, ChangeEvent } from 'react';
import { createPortal } from 'react-dom';
import { Search, Terminal, Command, Pin } from 'lucide-react';
import { pfeStore } from '../../stores/pfe.store.js';
import { launcherStore, useLauncherStore } from '../../stores/launcher.store.js';
import { usePins } from '../../stores/pins.store.js';
import {
  LAUNCHER_REGISTRY, CATEGORY_META,
  type LauncherCommand, type LauncherCategory, type LauncherStatus,
} from './launcherRegistry.js';
import type { Route } from '../../App.js';

interface LauncherProps {
  onNavigate: (r: Route) => void;
  onOpenIngest: () => void;
}

/** Sticky launcher button — top-right of TopBar.  This is the single
 *  visible system-level entry point. Removed when refactoring: standalone
 *  Quick Ingest and Files buttons. */
export function CyberpunkLauncherButton({ onClick }: { onClick: () => void }): JSX.Element {
  return (
    <button
      data-testid="cyberpunk-launcher-btn"
      onClick={onClick}
      title="Launcher (Ctrl+K)"
      className="relative h-8 px-3 flex items-center gap-2 rounded-sm
                 border border-cyan-300/60 bg-cyan-500/15 text-cyan-50
                 text-[11px] uppercase tracking-[0.20em] font-semibold
                 hover:bg-cyan-500/25 hover:border-cyan-200
                 shadow-[0_0_18px_rgba(34,211,238,0.18)]
                 hover:shadow-[0_0_28px_rgba(34,211,238,0.35)]
                 transition-all"
    >
      <span className="absolute -left-px top-0 bottom-0 w-px bg-cyan-300/80" />
      <Command className="h-3.5 w-3.5" strokeWidth={2} />
      <span>Launcher</span>
      <kbd className="ml-1 px-1 py-px text-[9px] tracking-normal text-cyan-200/80 border border-cyan-300/40 rounded-sm font-mono">⌘K</kbd>
    </button>
  );
}

/** The popover/command-palette mounted at App root via portal. */
export function CyberpunkLauncher({ onNavigate, onOpenIngest }: LauncherProps): JSX.Element | null {
  const { open } = useLauncherStore();
  const pins = usePins('launcher');
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Build the live command list with actions bound.
  const commands: LauncherCommand[] = useMemo(() => LAUNCHER_REGISTRY.map((c) => ({
    ...c,
    action: c.id === 'quick-ingest'
      ? () => { launcherStore.close(); onOpenIngest(); }
      : c.id === 'project-files'
        ? () => { launcherStore.close(); pfeStore.toggle(); }
        : c.routeTo
          ? () => { launcherStore.close(); onNavigate(c.routeTo as Route); }
          : () => { /* coming-soon — no-op, status pill informs the user */ },
  })), [onNavigate, onOpenIngest]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q
      ? commands.filter((c) => c.label.toLowerCase().includes(q) ||
                               c.description.toLowerCase().includes(q) ||
                               c.id.includes(q))
      : commands;
  }, [commands, query]);

  // Build "Pinned" pseudo-group at the top + the normal category groups.
  const grouped = useMemo(() => {
    const pinnedItems = filtered.filter((c) => pins.isPinned(c.id));
    const restByCat = new Map<LauncherCategory, LauncherCommand[]>();
    for (const c of filtered) {
      if (pins.isPinned(c.id)) continue;
      const arr = restByCat.get(c.category) ?? [];
      arr.push(c); restByCat.set(c.category, arr);
    }
    type Group = { key: string; label: string; items: LauncherCommand[]; pinned?: boolean };
    const out: Group[] = [];
    if (pinnedItems.length > 0) out.push({ key: 'pinned', label: 'Pinned', items: pinnedItems, pinned: true });
    for (const [cat, items] of [...restByCat.entries()].sort(
      (a, b) => CATEGORY_META[a[0]].order - CATEGORY_META[b[0]].order)) {
      out.push({ key: cat, label: CATEGORY_META[cat].label, items });
    }
    return out;
  }, [filtered, pins]);

  // Flat ordered list for keyboard navigation matches the on-screen order.
  const orderedFlat = useMemo(() => grouped.flatMap((g) => g.items), [grouped]);

  useEffect(() => { if (open) { setQuery(''); setCursor(0); setTimeout(() => inputRef.current?.focus(), 30); } }, [open]);
  useEffect(() => { setCursor(0); }, [query]);

  // Keyboard nav within the palette.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); launcherStore.close(); }
      if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(c + 1, orderedFlat.length - 1)); }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
      if (e.key === 'Enter') {
        e.preventDefault();
        const cmd = orderedFlat[cursor];
        if (cmd?.status === 'ready') cmd.action?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, orderedFlat, cursor]);

  if (!open) return null;

  return createPortal(
    <div data-testid="launcher-overlay"
      className="fixed inset-0 z-[10000] flex items-start justify-center pt-24 bg-black/70 backdrop-blur-sm p-6"
      onClick={() => launcherStore.close()}
    >
      <div className="w-full max-w-2xl bg-zinc-950 border border-cyan-300/60 rounded-sm
                      shadow-[0_0_40px_rgba(34,211,238,0.22)]"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 px-4 py-3 border-b border-cyan-400/20">
          <Terminal className="h-4 w-4 text-cyan-300" />
          <span className="text-[10px] uppercase tracking-[0.22em] text-cyan-300/80">BIM OPS · Launcher</span>
        </div>
        <div className="px-4 py-3 border-b border-cyan-400/10">
          <label className="flex items-center gap-2">
            <Search className="h-4 w-4 text-cyan-400/70" />
            <input
              ref={inputRef}
              data-testid="launcher-search"
              value={query}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
              placeholder="Search commands or paste anything…"
              className="w-full bg-transparent text-zinc-100 text-[13px] placeholder:text-zinc-600 focus:outline-none"
            />
            <kbd className="text-[10px] text-zinc-500 font-mono border border-zinc-700 rounded-sm px-1.5 py-0.5">esc</kbd>
          </label>
        </div>

        <ul data-testid="launcher-list" className="max-h-[55vh] overflow-y-auto py-1">
          {grouped.length === 0 && (
            <li className="px-4 py-6 text-[11px] text-zinc-500 text-center">No commands match.</li>
          )}
          {grouped.map((group) => (
            <li key={group.key} className="mb-1">
              <div className="px-4 py-1.5 flex items-center gap-1.5">
                <span className={
                  'text-[9.5px] uppercase tracking-[0.22em] ' +
                  (group.pinned ? 'text-amber-300' : 'text-zinc-500')
                }>
                  {group.label}
                </span>
                {group.pinned && <Pin className="h-2.5 w-2.5 text-amber-300" strokeWidth={2.5} />}
              </div>
              <ul>
                {group.items.map((c) => {
                  const idx    = orderedFlat.indexOf(c);
                  const active = idx === cursor;
                  const pinned = pins.isPinned(c.id);
                  return (
                    <li key={c.id} className="relative group">
                      <div
                        role="button"
                        tabIndex={c.status === 'ready' ? 0 : -1}
                        data-testid={`launcher-cmd-${c.id}`}
                        aria-disabled={c.status !== 'ready'}
                        onMouseEnter={() => setCursor(idx)}
                        onClick={() => { if (c.status === 'ready') c.action?.(); }}
                        onContextMenu={(e) => { e.preventDefault(); pins.toggle(c.id); }}
                        className={
                          'w-full flex items-center gap-3 px-4 py-2 text-left transition cursor-pointer select-none ' +
                          (active ? 'bg-cyan-500/10' : 'bg-transparent hover:bg-cyan-500/5') +
                          (c.status !== 'ready' ? ' opacity-50 cursor-not-allowed' : '')
                        }
                      >
                        {/* Edge pin ribbon (top-right) — solid amber when
                            pinned, low-opacity outline otherwise. */}
                        <span
                          aria-hidden="true"
                          className={
                            'pointer-events-none absolute top-0 right-0 w-3 h-3 ' +
                            (pinned
                              ? 'bg-gradient-to-bl from-amber-300/80 to-amber-300/0'
                              : 'opacity-0 group-hover:opacity-40 bg-gradient-to-bl from-zinc-500/40 to-transparent')
                          }
                        />
                        <c.icon className={'h-4 w-4 ' + (active ? 'text-cyan-200' : 'text-zinc-300')} strokeWidth={1.75} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-sans text-[12px] font-semibold text-zinc-100">{c.label}</span>
                            <StatusPill s={c.status} />
                            <span className="font-mono text-[9.5px] uppercase tracking-[0.16em] text-zinc-600">{c.service}</span>
                          </div>
                          <p className="font-sans text-[10.5px] text-zinc-500 truncate">{c.description}</p>
                        </div>
                        {c.shortcut && (
                          <kbd className="font-mono text-[9.5px] text-zinc-500 border border-zinc-700 rounded-sm px-1.5 py-0.5">
                            {c.shortcut}
                          </kbd>
                        )}
                        {/* Inline pin toggle button — visible on hover or
                            when already pinned. Stops the click bubbling
                            so the command itself doesn't run. */}
                        <button
                          type="button"
                          data-testid={`launcher-pin-${c.id}`}
                          onClick={(e) => { e.stopPropagation(); pins.toggle(c.id); }}
                          title={pinned ? 'Unpin from top' : 'Pin to top'}
                          className={
                            'flex-shrink-0 p-1 rounded-sm transition ' +
                            (pinned
                              ? 'text-amber-300 hover:text-amber-200'
                              : 'text-zinc-700 opacity-0 group-hover:opacity-100 hover:text-amber-300')
                          }
                        >
                          <Pin className={'h-3 w-3 ' + (pinned ? 'fill-amber-300' : '')} strokeWidth={2} />
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
        </ul>

        <footer className="px-4 py-2 flex items-center justify-between text-[9.5px] uppercase tracking-[0.18em] text-zinc-600 border-t border-cyan-400/10">
          <span>↑↓ navigate · ↵ run · right-click to pin · esc close</span>
          <span className="text-cyan-400/70">Single Command Surface</span>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

function StatusPill({ s }: { s: LauncherStatus }): JSX.Element {
  if (s === 'ready') {
    return <span className="text-[9px] uppercase tracking-[0.18em] px-1.5 py-px rounded-sm bg-emerald-500/15 text-emerald-300 border border-emerald-400/30">Ready</span>;
  }
  if (s === 'gated') {
    return <span className="text-[9px] uppercase tracking-[0.18em] px-1.5 py-px rounded-sm bg-amber-500/15 text-amber-300 border border-amber-400/30">Gated</span>;
  }
  return <span className="text-[9px] uppercase tracking-[0.18em] px-1.5 py-px rounded-sm bg-zinc-700/40 text-zinc-400 border border-zinc-600">Soon</span>;
}
